import React, { useState, useMemo } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MessageCircle, ExternalLink, Phone } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Maps a client nationality (ISO 2/3 letter or country name) to a WhatsApp message language.
 * Falls back to German (de) — main app language.
 */
function detectLang(nationality) {
  if (!nationality) return "de";
  const n = String(nationality).trim().toLowerCase();
  // 2-letter ISO
  const map2 = {
    ch: "de", de: "de", at: "de", li: "de",
    it: "it", sm: "it", va: "it",
    fr: "fr", be: "fr", lu: "fr", mc: "fr",
    pt: "pt", br: "pt", ao: "pt", mz: "pt", cv: "pt",
    es: "es", mx: "es", ar: "es", co: "es", cl: "es", pe: "es", uy: "es", ve: "es",
    gb: "en", us: "en", ie: "en", ca: "en", au: "en", nz: "en",
  };
  if (map2[n]) return map2[n];
  // Country names
  const lower = n;
  if (/(schweiz|switzerland|svizzera|svizra|deutsch|german|österreich|austria|liechten)/i.test(lower)) return "de";
  if (/(italia|italien|italy|italiano)/i.test(lower)) return "it";
  if (/(france|frankreich|francia|french|belg|luxemb|monaco)/i.test(lower)) return "fr";
  if (/(portug|brasil|brazil|angol|moçamb)/i.test(lower)) return "pt";
  if (/(spain|spanien|españa|spagna|mexico|méxico|argentin|chile|peru|colombi|venezu)/i.test(lower)) return "es";
  if (/(united kingdom|england|uk|usa|united states|ireland|canada|australia)/i.test(lower)) return "en";
  return "de";
}

/**
 * Builds a polite reminder message in the chosen language with placeholders filled in.
 */
function buildMessage({ lang, clientFirstName, company, policyNumber, insuranceType, endDate, daysLeft, advisorName }) {
  const D = daysLeft;
  const isExpired = D < 0;
  const dateStr = endDate || "—";
  const policy = policyNumber || "—";
  const name = clientFirstName || "";
  const advisor = advisorName || "Wertia Finanz";

  const T = {
    de: {
      hello: name ? `Sehr geehrte/r ${name},` : "Sehr geehrte/r Kunde,",
      expiring: `Wir möchten Sie freundlich daran erinnern, dass Ihre Versicherung *${company}* (${insuranceType}, Police-Nr. ${policy}) am *${dateStr}* abläuft — in ${D} Tagen.`,
      expiredMsg: `Wir möchten Sie darauf hinweisen, dass Ihre Versicherung *${company}* (${insuranceType}, Police-Nr. ${policy}) bereits seit dem *${dateStr}* abgelaufen ist.`,
      cta: "Gerne vereinbaren wir einen kurzen Termin, um Ihre Police zu erneuern oder zu optimieren.",
      sign: `Mit freundlichen Grüssen,\n${advisor}\nWertia Finanz GmbH`,
    },
    it: {
      hello: name ? `Gentile ${name},` : "Gentile Cliente,",
      expiring: `desideriamo ricordarLe che la Sua polizza *${company}* (${insuranceType}, n. ${policy}) scade il *${dateStr}* — tra ${D} giorni.`,
      expiredMsg: `desideriamo informarLa che la Sua polizza *${company}* (${insuranceType}, n. ${policy}) è scaduta in data *${dateStr}*.`,
      cta: "Volentieri fissiamo un breve appuntamento per rinnovare o ottimizzare la Sua copertura.",
      sign: `Cordiali saluti,\n${advisor}\nWertia Finanz GmbH`,
    },
    fr: {
      hello: name ? `Cher/Chère ${name},` : "Cher Client,",
      expiring: `nous souhaitons vous rappeler que votre assurance *${company}* (${insuranceType}, n° ${policy}) expire le *${dateStr}* — dans ${D} jours.`,
      expiredMsg: `nous vous informons que votre assurance *${company}* (${insuranceType}, n° ${policy}) a expiré le *${dateStr}*.`,
      cta: "Nous serions ravis de convenir d'un rendez-vous pour renouveler ou optimiser votre couverture.",
      sign: `Cordialement,\n${advisor}\nWertia Finanz GmbH`,
    },
    pt: {
      hello: name ? `Caro/a ${name},` : "Caro/a Cliente,",
      expiring: `gostaríamos de o(a) lembrar que o seu seguro *${company}* (${insuranceType}, apólice n.º ${policy}) expira a *${dateStr}* — em ${D} dias.`,
      expiredMsg: `gostaríamos de o(a) informar que o seu seguro *${company}* (${insuranceType}, apólice n.º ${policy}) expirou a *${dateStr}*.`,
      cta: "Teremos todo o gosto em marcar uma reunião breve para renovar ou otimizar a sua cobertura.",
      sign: `Com os melhores cumprimentos,\n${advisor}\nWertia Finanz GmbH`,
    },
    es: {
      hello: name ? `Estimado/a ${name},` : "Estimado Cliente,",
      expiring: `le recordamos que su seguro *${company}* (${insuranceType}, n.º ${policy}) vence el *${dateStr}* — en ${D} días.`,
      expiredMsg: `le informamos que su seguro *${company}* (${insuranceType}, n.º ${policy}) venció el *${dateStr}*.`,
      cta: "Con mucho gusto acordamos una breve cita para renovar u optimizar su cobertura.",
      sign: `Saludos cordiales,\n${advisor}\nWertia Finanz GmbH`,
    },
    en: {
      hello: name ? `Dear ${name},` : "Dear Client,",
      expiring: `we would like to remind you that your insurance *${company}* (${insuranceType}, policy no. ${policy}) expires on *${dateStr}* — in ${D} days.`,
      expiredMsg: `we would like to inform you that your insurance *${company}* (${insuranceType}, policy no. ${policy}) has expired on *${dateStr}*.`,
      cta: "We would be happy to schedule a brief meeting to renew or optimise your coverage.",
      sign: `Best regards,\n${advisor}\nWertia Finanz GmbH`,
    },
  };
  const t = T[lang] || T.de;
  const body = isExpired ? t.expiredMsg : t.expiring;
  return `${t.hello}\n\n${body}\n\n${t.cta}\n\n${t.sign}`;
}

/**
 * Normalises a phone number to international format used by wa.me (no leading +, no spaces, no dashes).
 * Returns null if not parseable.
 */
function normalizePhone(phone) {
  if (!phone) return null;
  let p = String(phone).trim().replace(/[\s\-().]/g, "");
  if (p.startsWith("00")) p = p.slice(2);
  else if (p.startsWith("+")) p = p.slice(1);
  if (!/^\d{8,15}$/.test(p)) return null;
  return p;
}

const LANG_LABEL = { de: "Deutsch", it: "Italiano", fr: "Français", pt: "Português", es: "Español", en: "English" };

/**
 * Dialog with editable WhatsApp reminder preview.
 */
export default function WhatsAppReminderDialog({ open, onOpenChange, client, policy, advisorName, onSent }) {
  const initialLang = useMemo(() => detectLang(client?.nationality), [client]);
  const [lang, setLang] = useState(initialLang);
  const daysLeft = useMemo(() => {
    if (!policy?.end_date) return 0;
    return Math.ceil((new Date(policy.end_date) - new Date()) / 86400000);
  }, [policy]);

  const defaultMsg = useMemo(() => buildMessage({
    lang,
    clientFirstName: client?.first_name,
    company: policy?.company,
    policyNumber: policy?.policy_number,
    insuranceType: policy?.insurance_type,
    endDate: policy?.end_date,
    daysLeft,
    advisorName,
  }), [lang, client, policy, daysLeft, advisorName]);

  const [text, setText] = useState(defaultMsg);
  React.useEffect(() => { setText(defaultMsg); }, [defaultMsg]);

  const normalizedPhone = normalizePhone(client?.phone);

  const openWhatsApp = () => {
    if (!normalizedPhone) {
      toast.error("Telefonnummer fehlt oder ungültig");
      return;
    }
    const encoded = encodeURIComponent(text);
    const url = `https://wa.me/${normalizedPhone}?text=${encoded}`;
    window.open(url, "_blank", "noopener,noreferrer");
    // Fire-and-forget log to backend
    if (policy?.id) {
      api.post(`/policies/${policy.id}/whatsapp-sent`, null, { params: { lang } })
        .then(() => { onSent && onSent(); })
        .catch(() => { /* silent */ });
    }
    onOpenChange(false);
  };

  if (!client || !policy) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-emerald-600" /> WhatsApp-Erinnerung
          </DialogTitle>
          <DialogDescription>
            Vorschau der Nachricht in der Kundensprache. WhatsApp Business wird geöffnet — Sie bestätigen den Versand manuell.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Phone className="w-3.5 h-3.5" strokeWidth={1.7} />
              <span className="font-mono">{client.phone || "—"}</span>
              {!normalizedPhone && (
                <span className="text-red-600 text-xs font-bold">· ungültig</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Sprache</Label>
              <select
                value={lang}
                onChange={(e) => setLang(e.target.value)}
                className="h-8 rounded-md border border-border bg-background text-xs px-2"
                data-testid="wa-lang-select"
              >
                {Object.entries(LANG_LABEL).map(([k, label]) => (
                  <option key={k} value={k}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="rounded-md border border-border bg-emerald-50/40 dark:bg-emerald-950/10 p-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1.5">Nachrichtenvorschau</div>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={11}
              className="font-mono text-xs leading-relaxed bg-card"
              data-testid="wa-message-textarea"
            />
            <div className="text-[10px] text-muted-foreground mt-1">
              {text.length} Zeichen · Empfohlene Länge: max. 1024 Zeichen
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button
            onClick={openWhatsApp}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            disabled={!normalizedPhone}
            data-testid="wa-open-btn"
          >
            <ExternalLink className="w-4 h-4 mr-1.5" /> WhatsApp öffnen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
